/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { DownloadOutlined, ReloadOutlined, SyncOutlined } from "@ant-design/icons";
import { sessionRecord } from "@buzzeasy/shared-frontend-utilities/sessionRecording";
import { Alert, Button, Descriptions, DescriptionsProps, Space, Tooltip, Typography } from "antd";
import { ReactElement, useEffect, useEffectEvent } from "react";
import { FallbackProps } from "react-error-boundary";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import environmentConfig from "../environmentConfig";
import { downloadSessionRecordingLog } from "../utils/miscUtils";
import { ComponentErrorBoundaryProps } from "./ComponentErrorBoundary";
import { ErrorBoundaryInfoContextValue } from "./ComponentErrorBoundary.ErrorInfoContext";

const { localDevelopmentMode } = environmentConfig;

const logErrorsInLocalDevelopment = false;

export type ErrorFallbackProps = ComponentErrorBoundaryProps & FallbackProps & {
  contextInfo: ErrorBoundaryInfoContextValue["info"];
};

export default function ErrorFallback({ error, resetErrorBoundary, componentName, componentNameTKey, contextInfo, displayInfo = [], allowPageRefresh }: ErrorFallbackProps): ReactElement {
  const { t } = useTranslation();

  const logErrorCaughtAsCustomEvent = useEffectEvent(() => {
    if (localDevelopmentMode && !logErrorsInLocalDevelopment)
      return;

    const payload = {
      "Component name": componentName || (componentNameTKey ? t(componentNameTKey, { lng: "en-US" }) : "unspecified"),
      ...Object.map(contextInfo, ([key, value]) => [key, value?.toString() ?? "NULL"]),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      "Versions": (window as any)._buzzeasy || "NULL",
    };

    sessionRecord.recordCustomEvent("error/caught", { error: serializeError(error), payload });
  });

  useEffect(() => { logErrorCaughtAsCustomEvent(); }, []);

  function downloadDebugLogs() {
    downloadSessionRecordingLog();
  }

  const descriptionItems = displayInfo
    ? displayInfo.map<NonNullable<DescriptionsProps["items"]>[0]>(({ key, label, value }) => ({ key, label, children: value ?? <em>null</em> }))
    : null;

  return (
    <ErrorFallback.Box>
      <Typography.Title level={3} style={{ margin: 0, textAlign: "center" }}>
        {t("errorHandling.anErrorOccurredInTheXComponent", { value: componentNameTKey ? t(componentNameTKey) : t("errorHandling.child") })}
      </Typography.Title>
      {
        descriptionItems &&
        <ErrorFallback.Descriptions
          size="small"
          column={1}
          items={descriptionItems}
        />
      }
      {
        error instanceof Error &&
        <ErrorFallback.Alert
          type="error"
          message={error.stack}
        />
      }
      <Space>
        <Button type="primary" icon={<SyncOutlined />} onClick={resetErrorBoundary}>{t("errorHandling.reloadComponent")}</Button>
        {
          allowPageRefresh &&
          <Button icon={<ReloadOutlined />} onClick={() => window.location.reload()}>{t("errorHandling.refreshPage")}</Button>
        }
      </Space>
      <Tooltip title={t("debugLogs.downloadDebugLogsHint")}>
        <Button size="small" icon={<DownloadOutlined />} onClick={downloadDebugLogs}>
          {t("debugLogs.downloadDebugLogs")}
        </Button>
      </Tooltip>
    </ErrorFallback.Box>
  );
}

function serializeError(error: unknown, seen = new WeakSet<object>()): unknown {
  if (!(error instanceof Error))
    return error;

  if (seen.has(error))
    return "[Circular]";
  seen.add(error);

  const result: Record<string, unknown> = { name: error.name };

  for (const key of Object.getOwnPropertyNames(error)) // message, stack, cause, custom props
    result[key] = serializeError((error as unknown as Record<string, unknown>)[key], seen);

  if (error instanceof AggregateError)
    result.errors = error.errors.map(e => serializeError(e, seen));

  return result;
}

ErrorFallback.Box = styled.div`
  width: 100%;
  height: 100%;
  padding: ${p => p.theme.paddingSM}px;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: ${p => p.theme.paddingSM}px;
`;

ErrorFallback.Alert = styled(Alert)`
  max-width: 100%;
  flex: 0 1 auto;
  min-height: 0;

  .ant-alert-message {
    max-height: 250px;
    overflow: auto;
    font-family: ${p => p.theme.fontFamilyCode};
    white-space: pre-wrap;
  }
`;

ErrorFallback.Descriptions = styled(Descriptions)`
  && {
    max-width: minmax(100%, 400px);

    table {
      width: unset;
    }

    .ant-descriptions-item {
      padding-bottom: 0;
    }

    .ant-descriptions-item-label,
    .ant-descriptions-item-content {
      flex: 0 0 auto;
      min-width: 0;
    }

    .ant-descriptions-item-content {
      cursor: default;
      font-family: ${p => p.theme.fontFamilyCode};
    }
  }
`;
